import { Network } from 'lucide-react';
import { StatusPill } from '@vnetwork/ui/data';
import { Badge, Switch, Skeleton } from '@vnetwork/ui/primitives';
import { Section } from '@vnetwork/ui/layout';
import {
  useCdnProviders,
  useToggleCdnProvider,
  type CdnProvider,
} from '@/api/manticore/cdn-providers';
import { CdnLoadBalancer } from './CdnLoadBalancer';
import { cdnProviderLabel } from './cdn-provider-display';
import styles from './ProviderStrip.module.css';

// ---------------------------------------------------------------------------
// Health → StatusPill tone mapping
// ---------------------------------------------------------------------------

type HealthTone = 'success' | 'warning' | 'muted';

function healthTone(health: CdnProvider['health']): HealthTone {
  if (health === 'active') return 'success';
  if (health === 'degraded') return 'warning';
  return 'muted';
}

function healthLabel(health: CdnProvider['health']): string {
  if (health === 'active') return 'Healthy';
  if (health === 'degraded') return 'Degraded';
  return 'Unknown';
}

// ---------------------------------------------------------------------------
// Single provider row
// ---------------------------------------------------------------------------

function ProviderRow({
  provider,
  onToggle,
  isToggling,
}: {
  provider: CdnProvider;
  onToggle: (enabled: boolean) => void;
  isToggling: boolean;
}) {
  const isActive = provider.status === 'active';

  return (
    <div className={styles.row}>
      <span className={`${styles.providerIcon}${provider.is_canonical ? ` ${styles.canonical}` : ''}`}>
        <Network size={16} />
      </span>

      <div className={styles.providerInfo}>
        <div className={styles.providerName}>{cdnProviderLabel(provider.provider_name)}</div>
        <div className={styles.providerMeta}>
          <span className={styles.scopeLabel}>{provider.provider_name}</span>
          {provider.is_canonical && (
            <span className={styles.primaryBadge}>Primary</span>
          )}
        </div>
        {/* Show a muted hint when this secondary has known upstream field limitations. */}
        {!provider.is_canonical &&
          (provider.capabilities?.upstream_unsupported_fields?.length ?? 0) > 0 && (
            <div className={styles.limitHint}>
              Origin limits: {provider.capabilities!.upstream_unsupported_fields.join(', ')}
            </div>
          )}
      </div>

      <div className={styles.controls}>
        {provider.health_message && (
          <span className={styles.healthMessage} title={provider.health_message}>
            {provider.health_message}
          </span>
        )}
        <StatusPill
          tone={healthTone(provider.health)}
          tooltip={provider.health_message}
        >
          {healthLabel(provider.health)}
        </StatusPill>
        {provider.is_canonical ? (
          // The primary provider can't be disabled — show a static "Always on"
          // chip rather than a disabled toggle (which reads as broken).
          <Badge tone="neutral" appearance="subtle" size="sm">
            Always on
          </Badge>
        ) : (
          <Switch
            size="sm"
            checked={isActive}
            disabled={isToggling}
            onCheckedChange={onToggle}
            aria-label={`${isActive ? 'Disable' : 'Enable'} ${cdnProviderLabel(provider.provider_name)}`}
            title={isActive ? 'Disable provider' : 'Enable provider'}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface ProviderStripProps {
  /** Umbrella service ID — drives `useCdnProviders`. */
  serviceId: string;
}

/**
 * Provider health + toggle strip for the CDN console.
 * Renders each backend provider as a row: name + scope badge, health StatusPill,
 * and an on/off Switch (canonical row is read-only). Mounts at the top of
 * ManticoreOverview (under the page header, above the traffic charts).
 */
export function ProviderStrip({ serviceId }: ProviderStripProps) {
  const { data: providers = [], isLoading, isError } = useCdnProviders(serviceId);
  const toggle = useToggleCdnProvider(serviceId);

  if (!serviceId) return null;

  return (
    <Section
      title="CDN Providers"
      description="Backend providers delivering traffic for this orchestrator. Toggle to enable or disable (canonical provider is always on)."
    >
      {isLoading ? (
        <div className={styles.strip}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={styles.skeletonRow}>
              <Skeleton style={{ width: 32, height: 32, borderRadius: 'var(--vn-radius-md)', flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-1)' }}>
                <Skeleton style={{ height: 14, width: '40%' }} />
                <Skeleton style={{ height: 12, width: '25%' }} />
              </div>
              <Skeleton style={{ height: 20, width: 80 }} />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)', padding: 'var(--vn-space-3) 0' }}>
          Failed to load providers. Please refresh.
        </div>
      ) : providers.length === 0 ? (
        <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', padding: 'var(--vn-space-3) 0' }}>
          No providers linked to this orchestrator yet.
        </div>
      ) : (
        <div className={styles.strip}>
          {providers.map((p) => (
            <ProviderRow
              key={p.provider_name}
              provider={p}
              isToggling={toggle.isPending}
              onToggle={(enabled) =>
                toggle.mutate({ provider: p.provider_name, enabled })
              }
            />
          ))}
          {/* Multi-CDN Load Balancer — last block of the SAME providers card,
              distributes traffic across the active backends above. */}
          <CdnLoadBalancer providers={providers} />
        </div>
      )}
    </Section>
  );
}
